import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import { getContent, getContentList, saveContent, type ContentEntity } from '../../lib/api/content'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const isNum = (v: unknown) => v != null && v !== '' && Number.isFinite(Number(v))
const numStr = (v: unknown) => (isNum(v) ? String(Number(v)) : '')
const numVal = (s: string) => (Number.isFinite(Number(s)) ? Number(s) : 0)

/* ── format <-> storage helpers (shared with the rates editor) ── */
const isoToDate = (v: unknown): string => {
  const m = String(v ?? '').match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}
const dateToIso = (d: string): string => (d ? `${d}T00:00:00Z` : '')
const hhmm = (v: unknown): string => {
  const m = String(v ?? '').match(/(\d{2}:\d{2})/)
  return m ? m[1] : ''
}
function htmlToText(html: unknown): string {
  return String(html ?? '')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+/g, ' ')
    .trim()
}
function textToHtml(text: string): string {
  const t = text.replace(/\r\n/g, '\n').trim()
  if (!t) return ''
  return t
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<p>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
    .join('')
}

const WEEKDAYS: [string, string][] = [
  ['1', 'Lun'],
  ['2', 'Mar'],
  ['3', 'Mer'],
  ['4', 'Gio'],
  ['5', 'Ven'],
  ['6', 'Sab'],
  ['7', 'Dom'],
]
const LANGS = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
] as const
type Lang = (typeof LANGS)[number]['code']

type Trans = { label: string; description: string }
type Validity = { fromDay: string; toDay: string; fromTime: string; toTime: string; weekDays: string[] }
type Form = {
  name: string
  transportId: string
  hours: string
  enabled: boolean
  validity: Validity
  tr: Record<Lang, Trans>
}
type Loaded = Record<Lang, ContentEntity>
const NEW = 'new'
const TABS = ['Generale', 'Validità', 'Traduzioni'] as const

function refId(ref: unknown): string {
  return ref && typeof ref === 'object' ? String((ref as { id?: unknown }).id ?? '') : String(ref ?? '')
}
function transOf(e: ContentEntity): Trans {
  const el = e.elements as El
  return { label: String(el.label ?? ''), description: htmlToText(el.description) }
}
function blankForm(): Form {
  return {
    name: '',
    transportId: '',
    hours: '',
    enabled: true,
    validity: { fromDay: '', toDay: '', fromTime: '', toTime: '', weekDays: [] },
    tr: { it: { label: '', description: '' }, en: { label: '', description: '' }, de: { label: '', description: '' } },
  }
}
function seedForm(loaded: Loaded): Form {
  const el = loaded.it.elements as El
  const v = (el.validity ?? {}) as El
  return {
    name: String(el.name ?? ''),
    transportId: refId(el.transportId),
    hours: numStr(el.hours),
    enabled: isYes(el.enabled),
    validity: {
      fromDay: isoToDate(v.fromDay),
      toDay: isoToDate(v.toDay),
      fromTime: hhmm(v.fromTime),
      toTime: hhmm(v.toTime),
      weekDays: String(v.weekDays ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    },
    tr: { it: transOf(loaded.it), en: transOf(loaded.en), de: transOf(loaded.de) },
  }
}

/* ── field atoms ── */
function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-faint">{hint}</span>}
    </label>
  )
}
const inputCls =
  'h-9 w-full rounded-md border border-line bg-card px-3 text-[13px] text-coal outline-none focus:border-brand-600 disabled:opacity-50'
const areaCls =
  'min-h-[72px] w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] text-coal outline-none focus:border-brand-600 disabled:opacity-50'

export function DurationEditDrawer({ durationId, onClose }: { durationId: string | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isNew = durationId === NEW
  const [tab, setTab] = useState(0)
  const [lang, setLang] = useState<Lang>('it')
  const [form, setForm] = useState<Form | null>(null)
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['duration', durationId, 'i18n'],
    queryFn: async (): Promise<Loaded> => {
      const [it, en, de] = await Promise.all([
        getContent('duration', durationId!, 'it'),
        getContent('duration', durationId!, 'en'),
        getContent('duration', durationId!, 'de'),
      ])
      return { it, en, de }
    },
    enabled: Boolean(durationId) && !isNew,
  })
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })

  useEffect(() => {
    if (isNew) {
      setLoaded(null)
      setForm(blankForm())
      setTab(0)
      setLang('it')
      setError(null)
    } else if (detail.data) {
      setLoaded(detail.data)
      setForm(seedForm(detail.data))
      setTab(0)
      setLang('it')
      setError(null)
    }
  }, [isNew, detail.data])

  const transportOptions = useMemo(
    () =>
      (transports.data?.data ?? []).map((t) => ({
        id: String(t.id),
        label: String((t.elements.name as string) ?? (t.elements.label as string) ?? t.id),
      })),
    [transports.data],
  )

  if (!durationId) return null
  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const patchV = (p: Partial<Validity>) => setForm((f) => (f ? { ...f, validity: { ...f.validity, ...p } } : f))
  const patchTr = (l: Lang, p: Partial<Trans>) =>
    setForm((f) => (f ? { ...f, tr: { ...f.tr, [l]: { ...f.tr[l], ...p } } } : f))
  const toggleWeekday = (d: string) =>
    setForm((f) => {
      if (!f) return f
      const has = f.validity.weekDays.includes(d)
      const weekDays = has ? f.validity.weekDays.filter((x) => x !== d) : [...f.validity.weekDays, d].sort()
      return { ...f, validity: { ...f.validity, weekDays } }
    })

  function applyShared(e: El) {
    e.name = form!.name.trim()
    e.transportId = { id: numVal(form!.transportId), modelId: 'transport' }
    e.hours = numVal(form!.hours)
    e.enabled = form!.enabled
    e.validity = {
      fromDay: dateToIso(form!.validity.fromDay),
      toDay: dateToIso(form!.validity.toDay),
      fromTime: form!.validity.fromTime || '',
      toTime: form!.validity.toTime || '',
      weekDays: form!.validity.weekDays.join(','),
    }
  }

  async function save() {
    if (!form) return
    if (!form.transportId) {
      setError('Seleziona un tipo di veicolo.')
      setTab(0)
      return
    }
    if (form.hours === '' || numVal(form.hours) <= 0) {
      setError('Inserisci una durata (ore) valida.')
      setTab(0)
      return
    }
    setError(null)
    setSaving(true)
    try {
      if (isNew || !loaded) {
        let id = ''
        for (const { code } of LANGS) {
          const e: El = {}
          applyShared(e)
          e.label = form.tr[code].label.trim() || form.name.trim()
          e.description = textToHtml(form.tr[code].description)
          const saved = await saveContent('duration', { id, modelId: 'duration', elements: e }, code)
          if (!id) id = saved.id
        }
      } else {
        for (const { code } of LANGS) {
          const base = loaded[code]
          const next: ContentEntity = { ...base, elements: { ...(base.elements as El) } }
          const e = next.elements as El
          applyShared(e)
          e.label = form.tr[code].label.trim() || form.name.trim()
          e.description = textToHtml(form.tr[code].description)
          await saveContent('duration', next, code)
        }
      }
      await qc.invalidateQueries({ queryKey: ['content', 'duration'] })
      await qc.invalidateQueries({ queryKey: ['duration', durationId] })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  const transLabel = transportOptions.find((t) => t.id === form?.transportId)?.label ?? ''

  return (
    <Drawer
      open={Boolean(durationId)}
      onClose={onClose}
      tone="brand"
      title={<span className="text-[15px] font-bold text-white">{isNew ? 'Nuova fascia di durata' : 'Modifica fascia di durata'}</span>}
      subtitle={form ? `${transLabel || '—'} · ${form.hours || '—'} ore` : 'Caricamento…'}
      footer={
        <div className="flex items-center gap-3">
          {error ? (
            <span className="text-[12px] font-medium text-venetian">{error}</span>
          ) : (
            <span className="text-[11.5px] text-muted">Salva scrive sul gestionale live.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50">
              Annulla
            </button>
            <button onClick={save} disabled={saving || !form} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? 'Crea' : 'Salva'}
            </button>
          </div>
        </div>
      }
    >
      {(!isNew && detail.isLoading) || !form ? (
        <div className="flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : !isNew && detail.isError ? (
        <div className="py-20 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare la fascia di durata.
        </div>
      ) : (
        <>
          <div className="mb-5 flex gap-1 border-b border-line">
            {TABS.map((label, i) => (
              <button
                key={label}
                onClick={() => setTab(i)}
                className={cn(
                  '-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition-colors',
                  tab === i ? 'border-brand-600 text-coal' : 'border-transparent text-muted hover:text-coal',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── GENERALE ── */}
          {tab === 0 && (
            <div className="space-y-4">
              <Field label="Nome" hint="Identificativo interno (non tradotto)">
                <input className={inputCls} value={form.name} disabled={saving} onChange={(e) => patch({ name: e.target.value })} />
              </Field>
              <Field label="Tipo di veicolo">
                <select className={inputCls} value={form.transportId} disabled={saving} onChange={(e) => patch({ transportId: e.target.value })}>
                  <option value="">— Seleziona —</option>
                  {transportOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="grid grid-cols-2 items-end gap-3">
                <Field label="Durata (ore)" hint="es. 24 = 1 giorno, 168 = 1 settimana">
                  <input type="number" min="0" className={inputCls} value={form.hours} disabled={saving} onChange={(e) => patch({ hours: e.target.value })} />
                </Field>
                <label className="flex h-9 items-center gap-2 text-[13px] text-coal">
                  <input type="checkbox" checked={form.enabled} disabled={saving} onChange={(e) => patch({ enabled: e.target.checked })} className="h-4 w-4 accent-brand-600" />
                  Attiva
                </label>
              </div>
            </div>
          )}

          {/* ── VALIDITÀ ── */}
          {tab === 1 && (
            <div className="space-y-4">
              <p className="text-[12.5px] text-muted">Finestra in cui la fascia è selezionabile. Lascia vuoto per “sempre valida”.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Da">
                  <input type="date" className={inputCls} value={form.validity.fromDay} disabled={saving} onChange={(e) => patchV({ fromDay: e.target.value })} />
                </Field>
                <Field label="A">
                  <input type="date" className={inputCls} value={form.validity.toDay} disabled={saving} onChange={(e) => patchV({ toDay: e.target.value })} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dalle ore">
                  <input type="time" className={inputCls} value={form.validity.fromTime} disabled={saving} onChange={(e) => patchV({ fromTime: e.target.value })} />
                </Field>
                <Field label="Fino alle ore">
                  <input type="time" className={inputCls} value={form.validity.toTime} disabled={saving} onChange={(e) => patchV({ toTime: e.target.value })} />
                </Field>
              </div>
              <Field label="Giorni della settimana">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map(([val, lbl]) => {
                    const on = form.validity.weekDays.includes(val)
                    return (
                      <button
                        key={val}
                        type="button"
                        disabled={saving}
                        onClick={() => toggleWeekday(val)}
                        className={cn('rounded-md border px-2.5 py-1.5 text-[12px] font-semibold transition-colors', on ? 'border-brand-600 bg-brand-600 text-white' : 'border-line bg-card text-muted hover:text-coal')}
                      >
                        {lbl}
                      </button>
                    )
                  })}
                </div>
              </Field>
            </div>
          )}

          {/* ── TRADUZIONI ── */}
          {tab === 2 && (
            <div className="space-y-3">
              <div className="flex gap-1 border-b border-line">
                {LANGS.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    className={cn(
                      '-mb-px border-b-2 px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                      lang === code ? 'border-brand-600 text-coal' : 'border-transparent text-muted hover:text-coal',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Field label="Label" hint="Etichetta mostrata sul sito (vuoto = usa il Nome)">
                <input className={inputCls} value={form.tr[lang].label} disabled={saving} onChange={(e) => patchTr(lang, { label: e.target.value })} />
              </Field>
              <Field label="Descrizione" hint="Testo semplice — una riga per paragrafo">
                <textarea className={areaCls} value={form.tr[lang].description} disabled={saving} onChange={(e) => patchTr(lang, { description: e.target.value })} />
              </Field>
            </div>
          )}
        </>
      )}
    </Drawer>
  )
}
